/**
 * Publish & Export to Google Sheets — feature E07-F06 contract.
 *
 * Shared by the api (the publish command + the googleSheets export module) and
 * the web Publish screen + success dialog. Feature-owned: re-exported from
 * `@eer/shared` by the serial Wire pass — it MUST NOT edit the shared barrel.
 *
 * Parity source (1:1 port, PM ground truth):
 *   - The Company-Research Stage-2 publish flow was NEVER deployed to legacy n8n
 *     prod, so the authoritative source is the research prototype
 *     `../EER app/prototype/assets/research.proto.js`
 *     (renderPublish / doPublish — lines 1166-1252): the Publish screen shows
 *     counts (accepted + Grade A/B/C), a needs-human warning, a gate/auto
 *     explainer, three checkboxes (Export to Sheets / Send to recruiter / Save
 *     to library — ONLY the last one checked by default, E07-F06-T07), and a
 *     success dialog with a clickable+copyable Sheets link and "Sent to <owner>
 *     · linked to <project>".
 *   - Google Sheets API surface: docs/source-n8n/integrations.md (Google APIs) +
 *     the googleapis service-account pattern already used by
 *     services/googleDriveDocs.ts (E08-F03).
 *
 * INVARIANTS encoded here (from the E07-F06 acceptance criteria):
 *   - publish exports ONLY review_status='accepted' rows, ranked by score DESC.
 *   - idempotent by request_id: a second publish reuses/updates the same
 *     sheet_url (never a second Sheet); research_requests.sheet_url +
 *     status='published' are set.
 *   - gate mode: publish allowed only after submit_review; a warning fires when
 *     an accepted row is still needs_human.
 *   - the export columns (Google Sheet AND the .xlsx download) are the EER
 *     researcher table (E07-F06-T08, docs/research/examples-analysis.md §2):
 *     Grade | Comments | Company | Website | Establishment Year | LinkedIn Page |
 *     Industry | Sub-Industry | Target Product | Specific Paragraph | State/City |
 *     Country | HQ Location | Global Company Size. Missing data = an EMPTY cell,
 *     never a placeholder. Country = the office country in the target location;
 *     HQ Location = the headquarters country (they may differ).
 *   - save-to-library is the ONLY block checked by DEFAULT; each of the three
 *     publish blocks (Sheets / email / library) is independently optional via
 *     its checkbox.
 *   - BOTH delivery blocks are non-fatal: they report `sheetsError` /`emailError`
 *     instead of rolling back a publish whose other blocks already succeeded
 *     (E07-F06-T06). "Publish failed" is reserved for a real refusal (404 / the
 *     review gate), never for an unavailable external integration.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Export column layout (the header, in order) — the EER researcher table
// ---------------------------------------------------------------------------
/**
 * The fixed export column order (E07-F06-T08) — the table EER researchers hand
 * to recruiters (7 of 8 real results, docs/research/examples-analysis.md §2 +
 * §3 row 1). Shared by the Google Sheets export AND the .xlsx download, so both
 * channels carry exactly the same columns. Used by the export module to build the
 * header row and the per-row cell vector so the order can never drift.
 *
 * (Replaces the T01 layout rank/score/6 criterion verdicts/evidence, which the
 * recruiters do not read.)
 */
export const PUBLISH_SHEET_COLUMNS = [
    'grade',
    'comments',
    'company',
    'website',
    'establishmentYear',
    'linkedinPage',
    'industry',
    'subIndustry',
    'targetProduct',
    'specificParagraph',
    'stateCity',
    'country',
    'hqLocation',
    'globalCompanySize',
];
/** Human header labels — verbatim the EER researcher table headers. */
export const PUBLISH_SHEET_HEADER = [
    'Grade',
    'Comments',
    'Company',
    'Website',
    'Establishment Year',
    'LinkedIn Page',
    'Industry',
    'Sub-Industry',
    'Target Product',
    'Specific Paragraph',
    'State/City',
    'Country',
    'HQ Location',
    'Global Company Size',
];
// ---------------------------------------------------------------------------
// .xlsx download (E07-F06-T08) — the file channel, works without Sheets
// ---------------------------------------------------------------------------
/** MIME type of the .xlsx download. */
export const PUBLISH_XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/** API path (under the api base) of the .xlsx download for a request. */
export function publishXlsxPath(requestId) {
    return `/research/${requestId}/publish/xlsx`;
}
/**
 * The download file name: the request title reduced to filesystem-safe
 * characters (`Acme / MDR: Israel` → `Acme MDR Israel.xlsx`). Falls back to
 * `research-<id>.xlsx` when nothing printable is left.
 */
export function publishXlsxFileName(title, requestId) {
    const safe = title
        .replace(/[^A-Za-z0-9 ._()-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);
    return `${safe || `research-${requestId}`}.xlsx`;
}
// ---------------------------------------------------------------------------
// Publish options (the three independent checkboxes)
// ---------------------------------------------------------------------------
/**
 * The three publish blocks, each independently toggleable (prototype renderPublish
 * three checkboxes). ONLY `saveToLibrary` is on by default.
 *
 * E07-F06-T07 (QA rejection 2026-08-18): the screen used to pre-check all three,
 * so a plain "Publish" click silently created a Google Sheet AND emailed the
 * recruiter. The item's acceptance criterion is explicit — «3 чекбокса … Save to
 * library — последний checked». The wire default matches the screen default so
 * there is exactly ONE truth: a body that omits `options` performs no external
 * side effect (no Sheet, no email) — only the library save. Turning a delivery
 * block ON is always a deliberate user action.
 */
export const publishOptionsSchema = z
    .object({
    exportToSheets: z.boolean().default(false),
    sendToRecruiter: z.boolean().default(false),
    saveToLibrary: z.boolean().default(true),
})
    .strict();
/**
 * Defaults applied when the body omits options entirely: save-to-library ONLY.
 * Side-effecting blocks (Sheets export, recruiter email) must be opted into.
 */
export const DEFAULT_PUBLISH_OPTIONS = {
    exportToSheets: false,
    sendToRecruiter: false,
    saveToLibrary: true,
};
// ---------------------------------------------------------------------------
// E07-F06-T10 — "send the list to sourcers" (To: sourcers, CC: teamlead + Himanshu)
// ---------------------------------------------------------------------------
/**
 * Staff address-book category whose ACTIVE contacts are offered as sourcer
 * recipients on the Publish screen (Admin → Address Book, category "Sourcer").
 */
export const PUBLISH_SOURCER_CATEGORY = 'sourcer';
/**
 * Staff address-book category whose ACTIVE contacts pre-fill the CC of the
 * sourcer email (Ksenia 19.10: the sourcing teamlead + Himanshu). The list is
 * configured in Admin → Address Book (category "Research Publish CC"); the
 * screen pre-fills it and the user may remove/add addresses per publish.
 */
export const PUBLISH_CC_CATEGORY = 'research_cc';
/** Upper bound on To/CC addresses in one sourcer email (abuse guard). */
export const PUBLISH_MAX_RECIPIENTS = 50;
/** A named recipient offered by the Publish screen (staff contact projection). */
export const publishRecipientSchema = z.object({
    name: z.string(),
    email: z.string(),
});
const recipientEmail = z.string().trim().email('invalid email address');
/**
 * The sourcer email block. PRESENT ⇒ the block is requested (one email, To: the
 * selected sourcers, CC: the edited CC list, the accepted list attached as .xlsx
 * + the Sheets link when there is one). ABSENT ⇒ no sourcer email — so a body
 * without it keeps the T07 "no external side effect by default" rule.
 */
export const publishSourcerEmailSchema = z
    .object({
    to: z.array(recipientEmail).min(1, 'select at least one sourcer').max(PUBLISH_MAX_RECIPIENTS),
    cc: z.array(recipientEmail).max(PUBLISH_MAX_RECIPIENTS).default([]),
})
    .strict();
/** POST /research/:id/publish body — the three checkboxes (all optional). */
export const publishRequestBodySchema = z
    .object({
    options: publishOptionsSchema.partial().optional(),
    /** E07-F06-T10 — opt-in sourcer email (To: sourcers, CC: teamlead + Himanshu). */
    sourcerEmail: publishSourcerEmailSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Publish summary (drives the Publish screen header counts)
// ---------------------------------------------------------------------------
/**
 * The counts the Publish screen renders (prototype renderPublish): total accepted
 * + per-grade (A/B/C) + how many accepted rows are still needs_human (drives the
 * amber warning block). Pure derivation from the accepted long-list rows.
 */
export const publishSummarySchema = z.object({
    accepted: z.number().int().min(0),
    gradeA: z.number().int().min(0),
    gradeB: z.number().int().min(0),
    gradeC: z.number().int().min(0),
    /** accepted rows still flagged needs_human → amber "review evidence" warning. */
    needsHumanFlagged: z.number().int().min(0),
    /**
     * E07-F04-T11 — accepted rows no AI check / Verify has touched (checkState
     * 'not_checked'); counted SEPARATELY from needsHumanFlagged (no overlap).
     */
    notCheckedFlagged: z.number().int().min(0).default(0),
    /** True when the request's review_mode is 'gate' (vs 'auto'). */
    gate: z.boolean(),
});
// ---------------------------------------------------------------------------
// Publish result (drives the success dialog)
// ---------------------------------------------------------------------------
/**
 * The success-dialog payload (prototype doPublish): the Sheets URL (clickable +
 * copyable), the recruiter the link was sent to and the linked project, plus how
 * many companies were saved to the reusable library. `idempotentReuse` is true
 * when this publish re-used an existing Sheet rather than creating a new one
 * (acceptance #2 of T02) — the dialog can surface "updated existing export".
 */
export const publishResultSchema = z.object({
    ok: z.literal(true),
    requestId: z.number().int().positive(),
    status: z.literal('published'),
    /** The Google Sheet URL (null when Sheets export was unchecked OR failed). */
    sheetUrl: z.string().nullable(),
    exported: z.number().int().min(0),
    /** True when the existing sheet_url was reused (idempotent re-publish). */
    idempotentReuse: z.boolean(),
    /** Recruiter (owner) the link was emailed to — null when email unchecked/absent. */
    sentToRecruiter: z.string().nullable(),
    /** The linked project title for the "linked to <project>" line — null if standalone. */
    linkedProject: z.string().nullable(),
    /** Companies whose used_in_count was incremented (0 when library unchecked). */
    savedToLibrary: z.number().int().min(0),
    /** Accepted rows still needs_human at publish time (echoed for the dialog warning). */
    needsHumanFlagged: z.number().int().min(0),
    /** E07-F04-T11 — accepted rows never checked at publish time (echoed, never blocks). */
    notCheckedFlagged: z.number().int().min(0).default(0),
    /**
     * Why the Google Sheets export did NOT produce a spreadsheet, when it WAS
     * requested (E07-F06-T06). Symmetric with `emailError` and for the same reason:
     * an unconfigured service account or a Google API hiccup used to throw a 409/500
     * out of the whole command, so a publish that also asked for save-to-library
     * lost that work behind a bare "Publish failed" toast — with the export block
     * pre-checked (pre-T07) that was EVERY publish. The unavailability is reported,
     * never silent: the screen renders it as an amber "the export did not run"
     * line next to an otherwise successful publish. null = nothing to report
     * (either not requested, or the sheet really was written → `sheetUrl` is set).
     *
     * One case reports a message WITH a non-null `sheetUrl`: the sheet was created
     * and filled, but Drive refused the "anyone with the link" permission (a common
     * Workspace sharing policy). The export succeeded; the warning tells the user
     * the recruiter may need to be granted access explicitly.
     */
    sheetsError: z.string().nullable(),
    /**
     * Why the recruiter email did NOT go out, when it was requested (E07-F06-T06).
     * The email block is DELIBERATELY non-fatal: a missing sender mailbox or a
     * Graph/SMTP hiccup must not roll back an otherwise successful publish (the
     * Sheet is already created, the library already saved). null = nothing to
     * report (either not requested, or actually sent → `sentToRecruiter` is set).
     */
    emailError: z.string().nullable(),
    /**
     * E07-F06-T10 — addresses the sourcer email went To (empty when the block was
     * not requested or did not go out). Defaulted so older payloads still parse.
     */
    sentToSourcers: z.array(z.string()).default([]),
    /** E07-F06-T10 — the CC the sourcer email carried (empty when not sent). */
    sourcersCc: z.array(z.string()).default([]),
    /**
     * E07-F06-T10 — why the sourcer email did NOT go out when it was requested.
     * Non-fatal, exactly like `emailError` (never rolls back the publish).
     */
    sourcersEmailError: z.string().nullable().default(null),
});
// ---------------------------------------------------------------------------
// GET /research/:id/publish — the pre-publish summary the screen loads
// ---------------------------------------------------------------------------
/** GET /research/:id/publish → 200 { ok, summary }. Drives the Publish screen. */
export const publishSummaryResponseSchema = z.object({
    ok: z.literal(true),
    requestId: z.number().int().positive(),
    title: z.string(),
    owner: z.string().nullable(),
    linkedProject: z.string().nullable(),
    /** Already-published sheet_url (so the screen can show "already published"). */
    sheetUrl: z.string().nullable(),
    status: z.string(),
    summary: publishSummarySchema,
    /**
     * Server-side availability of the Google Sheets transport (service-account
     * credentials present). E07-F06-T06: on an environment without them the export
     * block can only 409, so the screen must SAY so next to the checkbox instead of
     * letting the user walk into a dead-end "Publish failed".
     */
    sheetsConfigured: z.boolean(),
    /**
     * Whether a sender mailbox is resolvable for the acting user (personal → system
     * fallback). false ⇒ the "Send to recruiter" block cannot deliver; the screen
     * warns instead of silently doing nothing.
     */
    emailConfigured: z.boolean(),
    /**
     * E07-F06-T10 — active staff contacts of category `sourcer` (Address Book),
     * offered as To recipients of the sourcer email.
     */
    sourcers: z.array(publishRecipientSchema).default([]),
    /**
     * E07-F06-T10 — active staff contacts of category `research_cc` (the sourcing
     * teamlead + Himanshu): the DEFAULT CC, editable on the screen per publish.
     */
    defaultCc: z.array(publishRecipientSchema).default([]),
});
//# sourceMappingURL=research-publish.js.map